import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Snackbar } from '@mui/material';

const ToastContext = createContext(null);

/**
 * App-wide notifications. Pages call `toast.success('Saved')` instead of each
 * managing their own snackbar state. Also surfaces permission errors reported
 * by the API client.
 */
export const ToastProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState({ message: '', severity: 'success' });

  const show = useCallback((message, severity = 'success') => {
    setToast({ message, severity });
    setOpen(true);
  }, []);

  useEffect(() => {
    const onForbidden = (event) => show(event.detail, 'error');
    window.addEventListener('api:forbidden', onForbidden);
    return () => window.removeEventListener('api:forbidden', onForbidden);
  }, [show]);

  const value = useMemo(
    () => ({
      show,
      success: (message) => show(message, 'success'),
      error: (message) => show(message, 'error'),
      info: (message) => show(message, 'info'),
      warning: (message) => show(message, 'warning'),
    }),
    [show]
  );

  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={toast.severity === 'error' ? 6000 : 3500}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleClose}
          severity={toast.severity}
          variant="filled"
          elevation={6}
          sx={{ borderRadius: 2.5, alignItems: 'center', minWidth: 280 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
